// Detect names only when the answer or its source explicitly identifies them
// as scientific names. Do not italicize arbitrary English or higher taxa.
const BINOMIAL = String.raw`(?:[A-Z][a-z]+|[A-Z]\.)(?:[ \t]+|\u00a0)[a-z][a-z-]+`;

export function scientificNamesFrom(...texts: string[]): string[] {
  const names = new Set<string>();
  for (const raw of texts) {
    const text = raw.replace(/<[^>]*>/g, '').replace(/[*_`]/g, '');
    const re = new RegExp(String.raw`(?:学名|拉丁(?:学)?名|scientific\s+name)[ \t]*(?:为|是|叫做|[:：]|is|为[:：]|是[:：])?[ \t]*(${BINOMIAL})`, 'g');
    for (const match of text.matchAll(re)) names.add(match[1]);
  }
  return [...names];
}

interface HtmlNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HtmlNode[];
}

/** Plain-text views use the same explicit-name rules without parsing source HTML. */
export function scientificNameParts(text: string, ...sources: string[]): { text: string; italic: boolean }[] {
  return scientificNamePartsFor(text, scientificNamesFrom(text, ...sources));
}

/** Structured scientific-name fields can supply exact names without re-detecting them. */
export function scientificNamePartsFor(text: string, scientificNames: string[]): { text: string; italic: boolean }[] {
  const names = [...new Set(scientificNames.filter((name) => name.trim()))];
  if (!names.length) return [{ text, italic: false }];
  const pattern = names.sort((a, b) => b.length - a.length).map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const re = new RegExp(`(?<![A-Za-z])(?:${pattern})(?![A-Za-z-])`, 'g');
  const parts: { text: string; italic: boolean }[] = [];
  let offset = 0;
  for (const match of text.matchAll(re)) {
    if (match.index! > offset) parts.push({ text: text.slice(offset, match.index), italic: false });
    parts.push({ text: match[0], italic: true });
    offset = match.index! + match[0].length;
  }
  if (offset < text.length) parts.push({ text: text.slice(offset), italic: false });
  return parts;
}

/** Runs after Markdown/HTML parsing so links, code and existing italics stay intact. */
export function rehypeScientificNames(options: { names?: string[] } = {}) {
  return (tree: HtmlNode) => {
    const plain = (node: HtmlNode): string => node.value ?? node.children?.map(plain).join('') ?? '';
    const names = [...new Set([...(options.names ?? []), ...scientificNamesFrom(plain(tree))])];
    if (!names.length) return;
    const pattern = names.sort((a, b) => b.length - a.length).map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const re = new RegExp(`(?<![A-Za-z])(?:${pattern})(?![A-Za-z-])`, 'g');
    const skip = new Set(['em', 'i', 'code', 'pre', 'a', 'script', 'style', 'textarea']);
    function visit(node: HtmlNode) {
      if (!node.children || skip.has(node.tagName ?? '')) return;
      node.children = node.children.flatMap((child) => {
        if (child.type !== 'text' || !child.value) { visit(child); return [child]; }
        const parts: HtmlNode[] = [];
        let offset = 0;
        for (const match of child.value.matchAll(re)) {
          if (match.index! > offset) parts.push({ type: 'text', value: child.value.slice(offset, match.index) });
          parts.push({ type: 'element', tagName: 'em', properties: { className: ['scientific-name'] }, children: [{ type: 'text', value: match[0] }] });
          offset = match.index! + match[0].length;
        }
        if (!offset) return [child];
        if (offset < child.value.length) parts.push({ type: 'text', value: child.value.slice(offset) });
        return parts;
      });
    }
    visit(tree);
  };
}
